import React, { useState, useEffect, useMemo } from 'react'
import GardenCare from './GardenCare.jsx'
import PlantDiary from './PlantDiary.jsx'
import * as api from '../../services/api.js'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import { useThemeContext } from '../../context/ThemeContext.jsx'
import { formatDate, todayISO } from '../../utils/dateFormat.js'

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'learn', label: 'Learn' },
  { id: 'plan',  label: 'Plan' },
  { id: 'do',    label: 'Do' },
]

const GARDEN_QUOTES = [
  { text: 'To plant a garden is to believe in tomorrow.', author: 'Audrey Hepburn' },
  { text: 'The glory of gardening: hands in the dirt, head in the sun, heart with nature.', author: 'Alfred Austin' },
  { text: 'A garden is a grand teacher. It teaches patience and careful watchfulness.', author: 'Gertrude Jekyll' },
  { text: 'In every walk with nature, one receives far more than he seeks.', author: 'John Muir' },
  { text: 'Plants give us oxygen for the lungs and for the soul.', author: 'Linda Solegato' },
  { text: 'The love of gardening is a seed once sown that never dies.', author: 'Gertrude Jekyll' },
  { text: 'Gardens are not made by sitting in the shade.', author: 'Rudyard Kipling' },
  { text: 'One touch of nature makes the whole world kin.', author: 'William Shakespeare' },
  { text: 'Gardening is cheaper than therapy, and you get tomatoes.', author: 'Unknown' },
  { text: 'A garden requires patient labour and attention. Plants do not grow merely to satisfy ambitions.', author: 'Liberty Hyde Bailey' },
  { text: 'The greatest gift of the garden is the restoration of the five senses.', author: 'Hanna Rion' },
  { text: 'A weed is but an unloved flower.', author: 'Ella Wheeler Wilcox' },
]

const SEASONAL_TASKS = {
  spring: [
    'Start seeds indoors 6–8 weeks before the last frost',
    'Prepare raised beds with fresh compost or top-dress borders',
    'Divide and repot houseplants that have outgrown their pots',
    'Begin feeding plants regularly as new growth appears',
    'Check for overwintered pests and treat them early',
    'Prune roses and summer-flowering shrubs',
    'Sow fast-growing salad leaves outdoors once the soil warms',
    'Acclimatise tender plants gradually before moving them outside',
  ],
  summer: [
    'Water consistently — especially containers in warm weather',
    'Harvest regularly to keep plants productive',
    'Pinch out tomato sideshoots weekly',
    'Feed flowering and fruiting plants with a high-potassium feed',
    'Deadhead flowers to prolong the blooming season',
    'Watch for aphids, spider mites, and other pests',
    'Pot on anything rootbound before the heat peaks',
    'Mulch borders to conserve moisture and suppress weeds',
  ],
  autumn: [
    'Collect and store seeds from this year\'s best plants',
    'Bring tender plants indoors before the first frost',
    'Cut back perennials and tidy up beds',
    'Plant spring-flowering bulbs now',
    'Reduce watering as growth slows',
    'Apply mulch to protect roots over winter',
    'Clear spent vegetables and compost the debris',
    'Divide large clumps of established perennials',
  ],
  winter: [
    'Clean, oil, and sharpen garden tools',
    'Order seed catalogues and plan next year\'s growing',
    'Check stored bulbs and tubers for signs of rot',
    'Water houseplants much less frequently',
    'Protect cold-sensitive plants with horticultural fleece',
    'Sow sweet peas indoors in late January',
    'Clean out the greenhouse or windowsill growing space',
    'Make a wishlist of new plants you want to try this year',
  ],
}

const SEASON_META = {
  spring: { emoji: '🌱', label: 'Spring' },
  summer: { emoji: '☀️', label: 'Summer' },
  autumn: { emoji: '🍂', label: 'Autumn' },
  winter: { emoji: '❄️', label: 'Winter' },
}

const CARE_ACTIONS = [
  { id: 'watered',    label: 'Watered',    icon: '💧' },
  { id: 'talked',     label: 'Talked',     icon: '🗣️' },
  { id: 'fertilized', label: 'Fertilized', icon: '🌱' },
  { id: 'pruned',     label: 'Pruned',     icon: '✂️' },
  { id: 'repotted',   label: 'Repotted',   icon: '🪴' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function currentSeason() {
  const m = new Date().getMonth() + 1
  if (m >= 3 && m <= 5) return 'spring'
  if (m >= 6 && m <= 8) return 'summer'
  if (m >= 9 && m <= 11) return 'autumn'
  return 'winter'
}

function dailyQuote() {
  const now = new Date()
  const start = new Date(now.getFullYear(), 0, 0)
  const dayOfYear = Math.floor((now - start) / 86400000)
  return GARDEN_QUOTES[dayOfYear % GARDEN_QUOTES.length]
}

function imgPrefix(themeName) {
  if (themeName === 'rainy') return 'rainy'
  if (themeName === 'cafe')  return 'cafe'
  if (themeName === 'quest') return 'quest'
  return 'general'
}

function stageImg(pfx, stage) {
  if (stage === 1) return `/images/${pfx}-seedling.png`
  if (stage === 2) return `/images/${pfx}-mid-flower.png`
  return `/images/${pfx}-full-flower.png`
}

function stageLabel(stage) {
  return stage === 1 ? 'Seedling' : stage === 2 ? 'Growing' : 'Fully Grown'
}

function encourageText(count) {
  if (count === 0) return 'Your plant is waiting for some love today — even just saying hello counts!'
  if (count === 1) return 'Good start! Every bit of care adds up. Your plant notices.'
  if (count === 2) return 'Lovely work. Talking, singing, or watering — all count.'
  if (count === 3) return 'Your plant is thriving with your attention today!'
  if (count === 4) return "Almost a full day of care — you're an amazing plant parent!"
  return 'Perfect care day! Your garden is so lucky to have you.'
}

// ── PlantCareProgress ─────────────────────────────────────────────────────────

function PlantCareProgress({ careLog }) {
  const FREQUENCY = 7
  const today = todayISO()

  const lastWatered = useMemo(() => {
    const dates = Object.keys(careLog)
      .filter(d => (careLog[d] || []).includes('watered'))
      .sort()
    return dates.length ? dates[dates.length - 1] : null
  }, [careLog])

  if (!lastWatered) {
    return (
      <div className="plant-progress">
        <span className="plant-progress-icon">💧</span>
        <div className="plant-progress-body">
          <div className="plant-progress-label">Never watered</div>
          <div className="plant-progress-sub">Tap Watered above when you give your plants a drink.</div>
        </div>
      </div>
    )
  }

  const daysSince = Math.floor((new Date(today) - new Date(lastWatered)) / 86400000)
  const daysUntil = FREQUENCY - daysSince
  const isOverdue = daysUntil <= 0
  const barPct    = Math.min(100, Math.round((daysSince / FREQUENCY) * 100))

  return (
    <div className="plant-progress">
      <span className="plant-progress-icon">💧</span>
      <div className="plant-progress-body">
        <div className="plant-progress-label">
          {isOverdue
            ? `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''}`
            : daysUntil === 0
            ? 'Water today!'
            : `Next watering in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`}
        </div>
        <div className="plant-progress-sub">
          Last watered {formatDate(lastWatered)}
        </div>
        <div className="plant-progress-bar-outer">
          <div
            className={`plant-progress-bar-inner${isOverdue ? ' overdue' : ''}`}
            style={{ width: `${barPct}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// ── CareTrackerHero ───────────────────────────────────────────────────────────

function CareTrackerHero({ careLog, onCareLog }) {
  const { themeName } = useThemeContext()
  const pfx = imgPrefix(themeName)

  const totalDays = useMemo(
    () => Object.keys(careLog).filter(d => (careLog[d] || []).length > 0).length,
    [careLog]
  )

  const stage    = totalDays < 8 ? 1 : totalDays < 21 ? 2 : 3
  const progress = stage === 1 ? Math.round((totalDays / 8) * 100)
    : stage === 2 ? Math.round(((totalDays - 8) / 13) * 100)
    : 100

  const today        = todayISO()
  const todayActions = careLog[today] || []

  function toggleAction(actionId) {
    const current = careLog[today] || []
    const updated  = current.includes(actionId)
      ? current.filter(a => a !== actionId)
      : [...current, actionId]
    onCareLog({ ...careLog, [today]: updated })
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div className="garden-hero-area">
        <div className="garden-mascot-wrap">
          <img
            className="garden-mascot-img"
            src={stageImg(pfx, stage)}
            alt={stageLabel(stage)}
          />
          <span className="garden-stage-label">{stageLabel(stage)}</span>
        </div>

        <div className="garden-care-side">
          <div className="garden-care-title">Today's plant care</div>
          <div className="garden-care-row">
            {CARE_ACTIONS.map(a => (
              <button
                key={a.id}
                className={`garden-care-btn${todayActions.includes(a.id) ? ' ticked' : ''}`}
                onClick={() => toggleAction(a.id)}
              >
                <span className="garden-care-icon">{a.icon}</span>
                {a.label}
              </button>
            ))}
          </div>
          <div className="garden-progress-bar-wrap">
            <div className="garden-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <p className="garden-encourage">{encourageText(todayActions.length)}</p>
        </div>
      </div>

      <PlantCareProgress careLog={careLog} />
    </div>
  )
}

// ── GardenJournalSection ──────────────────────────────────────────────────────

function GardenJournalSection({ entries, onEntries }) {
  const [draft,    setDraft]    = useState('')
  const [expanded, setExpanded] = useState(false)

  const SHOW_MAX = 3
  const shown = expanded ? entries : entries.slice(0, SHOW_MAX)

  function saveEntry() {
    const text = draft.trim()
    if (!text) return
    const next = [{ id: Date.now(), date: todayISO(), text }, ...entries]
    onEntries(next)
    setDraft('')
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{
        fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em',
        color: 'var(--stone)', marginBottom: '0.75rem',
      }}>
        Garden Journal
      </div>

      <textarea
        style={{
          width: '100%', boxSizing: 'border-box',
          background: 'var(--bg)', border: '1px solid var(--stone)',
          borderRadius: 10, padding: '10px 12px', fontSize: '0.85rem',
          fontFamily: 'var(--font-sans)', color: 'var(--ink)',
          resize: 'vertical', minHeight: 80, lineHeight: 1.6, display: 'block',
        }}
        placeholder="Write your garden thoughts, observations, or plans…"
        value={draft}
        onChange={e => setDraft(e.target.value)}
      />
      <button
        onClick={saveEntry}
        disabled={!draft.trim()}
        style={{
          marginTop: 8,
          background: draft.trim() ? 'var(--accent)' : 'var(--surf)',
          color: draft.trim() ? 'white' : 'var(--stone)',
          border: '1px solid var(--stone)', borderRadius: 8,
          padding: '6px 18px', fontSize: '0.8rem', fontFamily: 'var(--font-sans)',
          cursor: draft.trim() ? 'pointer' : 'default', transition: 'all 0.15s',
        }}
      >
        Save Entry
      </button>

      {entries.length > 0 && (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map(entry => (
            <div
              key={entry.id}
              style={{
                background: 'var(--surf)', borderRadius: 10,
                padding: '10px 12px', border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4,
              }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--stone)', fontWeight: 600 }}>
                  {formatDate(entry.date)}
                </span>
                <button
                  onClick={() => onEntries(entries.filter(e => e.id !== entry.id))}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--stone)', fontSize: '0.7rem', padding: '0 2px',
                  }}
                  title="Delete entry"
                >✕</button>
              </div>
              <p style={{
                fontSize: '0.84rem', color: 'var(--bark)',
                lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap',
              }}>
                {entry.text}
              </p>
            </div>
          ))}
          {entries.length > SHOW_MAX && (
            <button
              className="wb-show-more"
              onClick={() => setExpanded(e => !e)}
            >
              {expanded ? 'Show less' : `Show ${entries.length - SHOW_MAX} more`}
            </button>
          )}
        </div>
      )}

      {entries.length === 0 && (
        <p style={{
          marginTop: '0.75rem', fontSize: '0.78rem',
          color: 'var(--stone)', fontStyle: 'italic',
        }}>
          No entries yet — write something above and press Save Entry.
        </p>
      )}
    </div>
  )
}

// ── GardenTasks ───────────────────────────────────────────────────────────────

function GardenTasks() {
  const season = currentSeason()
  const tasks  = SEASONAL_TASKS[season]
  const meta   = SEASON_META[season]

  const [data,    setData]    = useState({ checked: {}, custom: [], seasonalEdits: {}, seasonalDeleted: [] })
  const [newTask, setNewTask] = useState('')
  const [editing, setEditing] = useState(null) // { type:'custom'|'seasonal', id, value }

  useEffect(() => {
    api.getWalkState('garden-tasks').then(d => setData({
      checked: {}, custom: [], seasonalEdits: {}, seasonalDeleted: [], ...(d || {}),
    }))
  }, [])

  function save(next) {
    setData(next)
    api.saveWalkState('garden-tasks', next)
  }

  // Custom task helpers
  function addCustomTask() {
    const text = newTask.trim()
    if (!text) return
    save({ ...data, custom: [{ id: Date.now(), text, done: false }, ...data.custom] })
    setNewTask('')
  }

  function toggleCustom(id) {
    save({ ...data, custom: data.custom.map(t => t.id === id ? { ...t, done: !t.done } : t) })
  }

  function deleteCustomTask(id) {
    save({ ...data, custom: data.custom.filter(t => t.id !== id) })
  }

  function commitEditCustom() {
    if (!editing || editing.type !== 'custom') return
    save({ ...data, custom: data.custom.map(t => t.id === editing.id ? { ...t, text: editing.value } : t) })
    setEditing(null)
  }

  // Seasonal task helpers
  function toggleSeasonal(idx) {
    const key = `${season}:${idx}`
    save({ ...data, checked: { ...data.checked, [key]: !data.checked[key] } })
  }

  function deleteSeasonal(idx) {
    const key = `${season}:${idx}`
    const deleted = [...(data.seasonalDeleted || [])]
    if (!deleted.includes(key)) deleted.push(key)
    save({ ...data, seasonalDeleted: deleted })
  }

  function commitEditSeasonal() {
    if (!editing || editing.type !== 'seasonal') return
    save({ ...data, seasonalEdits: { ...data.seasonalEdits, [editing.id]: editing.value } })
    setEditing(null)
  }

  function clearSeason() {
    const next = { ...data.checked }
    Object.keys(next).forEach(k => { if (k.startsWith(season + ':')) delete next[k] })
    save({ ...data, checked: next })
  }

  const seasonalDeleted  = data.seasonalDeleted || []
  const seasonalEdits    = data.seasonalEdits || {}
  const activeSeasonal   = tasks.filter((_, i) => !seasonalDeleted.includes(`${season}:${i}`))
  const seasonChecked    = activeSeasonal.filter((_, i) => {
    const origIdx = tasks.indexOf(activeSeasonal[i])
    return !!data.checked[`${season}:${origIdx}`]
  }).length

  const totalTasks  = data.custom.length + activeSeasonal.length
  const totalDone   = data.custom.filter(t => t.done).length + seasonChecked
  const progress    = totalTasks > 0 ? Math.round((totalDone / totalTasks) * 100) : 0

  const checkStyle = done => ({
    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
    background: done ? 'var(--sage)' : 'transparent',
    border: `2px solid ${done ? 'var(--sage)' : 'var(--stone)'}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.6rem', color: 'white', marginTop: 2,
    cursor: 'pointer', transition: 'all 0.15s',
  })

  const rowStyle = done => ({
    display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
    padding: '0.6rem 0.75rem',
    background: done ? 'rgba(82,169,106,0.07)' : 'var(--surf)',
    borderRadius: 10,
    border: `1px solid ${done ? 'rgba(82,169,106,0.18)' : 'transparent'}`,
  })

  const textStyle = done => ({
    fontSize: '0.88rem', color: done ? 'var(--stone)' : 'var(--ink)',
    lineHeight: 1.55, textDecoration: done ? 'line-through' : 'none',
    opacity: done ? 0.7 : 1, flex: 1, transition: 'all 0.15s',
  })

  const iconBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--stone)', fontSize: '0.65rem', padding: '0 2px',
    flexShrink: 0, lineHeight: 1,
  }

  return (
    <div>
      {/* ── Overall progress ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1, fontSize: '0.8rem', color: 'var(--stone)' }}>
          {totalDone} of {totalTasks} tasks done
        </div>
        {seasonChecked > 0 && (
          <button onClick={clearSeason} style={{
            background: 'none', border: '1px solid var(--stone)', borderRadius: 8,
            padding: '3px 10px', fontSize: '0.73rem', color: 'var(--stone)',
            cursor: 'pointer', fontFamily: 'var(--font-sans)',
          }}>
            Clear season
          </button>
        )}
      </div>
      <div className="garden-progress-bar-wrap" style={{ marginBottom: '1.5rem' }}>
        <div className="garden-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {/* ── My Tasks (custom — above seasonal) ── */}
      <div className="do-section">
        <div className="do-section__header">
          <span className="do-section__title">My Tasks</span>
        </div>

        {/* Add task input */}
        <div className="add-task-inline" style={{ marginBottom: '0.6rem' }}>
          <input
            type="text"
            value={newTask}
            onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustomTask()}
            placeholder="Add a task…"
          />
          <button
            onClick={addCustomTask}
            disabled={!newTask.trim()}
            style={{
              background: newTask.trim() ? 'var(--accent)' : 'var(--surf)',
              color: newTask.trim() ? 'white' : 'var(--stone)',
              border: '1px solid var(--stone)', borderRadius: 8,
              padding: '5px 14px', fontSize: '0.78rem', fontFamily: 'var(--font-sans)',
              cursor: newTask.trim() ? 'pointer' : 'default', transition: 'all 0.15s', flexShrink: 0,
            }}
          >
            Add
          </button>
        </div>

        {data.custom.length === 0 && (
          <p style={{ fontSize: '0.8rem', color: 'var(--stone)', fontStyle: 'italic', padding: '0.25rem 0 0.5rem' }}>
            No custom tasks yet — add one above.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {data.custom.map(t => (
            <div key={t.id} style={rowStyle(t.done)}>
              <span style={checkStyle(t.done)} onClick={() => toggleCustom(t.id)}>
                {t.done ? '✓' : ''}
              </span>
              {editing?.type === 'custom' && editing.id === t.id ? (
                <>
                  <input
                    className="task-item__edit-input"
                    value={editing.value}
                    onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitEditCustom()
                      if (e.key === 'Escape') setEditing(null)
                    }}
                    autoFocus
                  />
                  <button style={iconBtn} onClick={commitEditCustom} title="Save">✓</button>
                  <button style={iconBtn} onClick={() => setEditing(null)} title="Cancel">✕</button>
                </>
              ) : (
                <>
                  <span style={{ ...textStyle(t.done), cursor: 'pointer' }} onClick={() => toggleCustom(t.id)}>
                    {t.text}
                  </span>
                  <button
                    style={iconBtn}
                    onClick={() => setEditing({ type: 'custom', id: t.id, value: t.text })}
                    title="Edit"
                  >✏️</button>
                  <button style={iconBtn} onClick={() => deleteCustomTask(t.id)} title="Remove">✕</button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <hr className="do-section-divider" />

      {/* ── Seasonal Tasks (below custom) ── */}
      <div className="do-section">
        <div className="do-section__header">
          <span className="do-section__title">
            {meta.emoji} {meta.label} Checklist
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {tasks.map((originalText, i) => {
            const key  = `${season}:${i}`
            if (seasonalDeleted.includes(key)) return null
            const text = seasonalEdits[key] || originalText
            const done = !!data.checked[key]

            return (
              <div key={i} style={rowStyle(done)}>
                <span style={checkStyle(done)} onClick={() => toggleSeasonal(i)}>
                  {done ? '✓' : ''}
                </span>
                {editing?.type === 'seasonal' && editing.id === key ? (
                  <>
                    <input
                      className="task-item__edit-input"
                      value={editing.value}
                      onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitEditSeasonal()
                        if (e.key === 'Escape') setEditing(null)
                      }}
                      autoFocus
                    />
                    <button style={iconBtn} onClick={commitEditSeasonal} title="Save">✓</button>
                    <button style={iconBtn} onClick={() => setEditing(null)} title="Cancel">✕</button>
                  </>
                ) : (
                  <>
                    <span
                      style={{ ...textStyle(done), cursor: 'pointer' }}
                      onClick={() => toggleSeasonal(i)}
                    >
                      {text}
                    </span>
                    <button
                      style={iconBtn}
                      onClick={() => setEditing({ type: 'seasonal', id: key, value: text })}
                      title="Edit"
                    >✏️</button>
                    <button style={iconBtn} onClick={() => deleteSeasonal(i)} title="Remove">✕</button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── DoTab ─────────────────────────────────────────────────────────────────────

function DoTab() {
  const [careLog,  setCareLog]  = useState({})
  const [journal,  setJournal]  = useState([])

  useEffect(() => {
    api.getGardenCare().then(d => setCareLog(d || {}))
    api.getGardenJournal().then(d => setJournal(d || []))
  }, [])

  function handleCareLog(next) {
    setCareLog(next)
    api.saveGardenCare(next)
  }

  function handleJournal(next) {
    setJournal(next)
    api.saveGardenJournal(next)
  }

  return (
    <div>
      <CareTrackerHero careLog={careLog} onCareLog={handleCareLog} />
      <GardenJournalSection entries={journal} onEntries={handleJournal} />
      <hr className="do-section-divider" />
      <GardenTasks />
    </div>
  )
}

// ── Main Gardening page ───────────────────────────────────────────────────────

export default function Gardening() {
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const t = p.get('tab')
    return TABS.find(x => x.id === t) ? t : 'learn'
  })

  const quote = dailyQuote()

  function goTab(id) {
    setTab(id)
    history.replaceState(null, '', `?tab=${id}`)
  }

  return (
    <div className="page active">
      <div className="page-inner">
        <div className="hero">
          <p className="hero-eyebrow">My Life</p>
          <h1>Gardening</h1>
          <hr className="hero-rule" />
          <SectionWelcome text="Every plant is a small commitment to care. This is your space to tend, learn, and grow." />
        </div>

        {/* Daily quote card */}
        <div className="garden-quote-card">
          <div className="garden-quote-text">{quote.text}</div>
          <div className="garden-quote-author">— {quote.author}</div>
        </div>

        <div className="cooking-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`cooking-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => goTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'learn' && <GardenCare />}
        {tab === 'plan'  && <PlantDiary />}
        {tab === 'do'    && <DoTab />}
      </div>
    </div>
  )
}
